import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { X, Users, Building } from 'lucide-react';

// ---------------------------------------------------------------------------
// FACTURACIÓN de Corredata a sus clientes, y la ficha completa de un cliente ("Ver").
//
// De dónde sale lo que se cobra: el master de cada corretaje cambia su plan, agrega
// usuarios extra o compra paquetes de IA desde SU sistema. Cada cosa queda en
// `plan_cambios` / `ia_paquetes` con `facturado = false` ("consume ahora, se factura
// después"). Aquí Corredata EMITE el estado de cuenta del mes (mensualidad por adelantado
// + esos ajustes + ITBMS), registra cuándo pagó el cliente y anota el N° de la factura
// fiscal que emitió en la DGI. El cliente ve todo eso —solo lo suyo— en su "Mi Plan" y
// descarga el mismo PDF.
//
// Reglas (Ricardo, 2026-09-21): cobro ADELANTADO · ITBMS configurable (7%) · el documento
// es un ESTADO DE CUENTA, no la factura fiscal. Emitir y anular son funciones SQL del
// control plane (todo o nada): ver docs/corredata/control-plane/schema-facturacion.sql.
// ---------------------------------------------------------------------------

const GB = 1024 ** 3;
// Espacio legible: en MB mientras no llegue a 1 GB. Contra el plan cuentan los ARCHIVOS;
// la base de datos se muestra aparte, informativa (ver medirEspacio en _plan.js).
const fmtEspacio = gb => gb >= 1 ? `${(Math.round(gb * 10) / 10).toLocaleString('es-PA')} GB` : `${Math.max(0, Math.round(gb * 1024)).toLocaleString('es-PA')} MB`;
// Borra la FICHA de un cliente del portal. La bitácora (`eventos`) y `soporte_accesos` lo
// referencian sin cascada: se sueltan (cliente_id = null) para conservar el historial en vez
// de perderlo. Un cliente con estados de cuenta emitidos NO se borra (la base lo impide, y
// está bien: ya se le facturó).
export async function borrarFichaCliente(id) {
  for (const tabla of ['eventos', 'soporte_accesos']) {
    const { error } = await supabase.from(tabla).update({ cliente_id: null }).eq('cliente_id', id);
    if (error) return { error: `No se pudo soltar su historial (${tabla}): ${error.message}` };
  }
  const { error } = await supabase.from('clientes').delete().eq('id', id);
  if (error) {
    return { error: /estados_cuenta/.test(error.message) ? 'Tiene estados de cuenta emitidos: no se borra un cliente al que ya se le facturó.' : error.message };
  }
  return { ok: true };
}

export const usd = (n) => `US$${(Math.round((Number(n) || 0) * 100) / 100).toFixed(2)}`;
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const nombrePeriodo = (p) => { const [y, m] = String(p || '').split('-'); return m ? `${MESES[Number(m) - 1] || m} ${y}` : (p || '—'); };
const fechaCorta = (f) => { const [y, m, d] = String(f || '').slice(0, 10).split('-'); return d ? `${d}/${m}/${y}` : '—'; };
const hoyPanama = () => new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 10);
const FORMAS_PAGO = ['Transferencia / ACH', 'Yappy', 'Tarjeta', 'Cheque', 'Efectivo', 'Otro'];
// Primera frase del detalle, sin paréntesis ni montos: es el concepto que va al documento.
const conceptoDe = (detalle) => String(detalle || '').split('. ')[0].replace(/\s*\(.*$/, '').replace(/\.$/, '');

// Mensualidad vigente = plan + usuarios extra. NO es una fila guardada: se calcula.
const precioExtra = (p) => (p?.precio_usuario_extra == null ? 9 : Number(p.precio_usuario_extra) || 0);
export const mensualidadDe = (c) => {
  const p = c?.planes;
  if (!p) return 0;
  return r2((Number(p.precio_mensual) || 0) + (Number(c.usuarios_extra) || 0) * precioExtra(p));
};

const SEL_CLIENTE = 'id, nombre, estado, plan_id, usuarios_extra, contacto_nombre, contacto_email, planes(nombre, precio_mensual, precio_usuario_extra)';

// Lo que cada cliente aceptó y AÚN NO se le cobra. Un solo lector para toda la pantalla.
export async function cargarPendientes(clienteId = null) {
  let q1 = supabase.from('plan_cambios').select('id, cliente_id, tipo, periodo, monto, detalle, solicitado_por, en_prueba, creado_en').eq('facturado', false);
  let q2 = supabase.from('ia_paquetes').select('id, cliente_id, periodo, analisis, precio, origen, creado_en').eq('facturado', false);
  if (clienteId) { q1 = q1.eq('cliente_id', clienteId); q2 = q2.eq('cliente_id', clienteId); }
  const [a, b] = await Promise.all([q1.order('creado_en'), q2.order('creado_en')]);
  if (a.error) throw new Error(a.error.message);
  if (b.error) throw new Error(b.error.message);
  const porCliente = {};
  const meter = (cid, l) => { (porCliente[cid] = porCliente[cid] || []).push(l); };
  for (const c of (a.data || [])) {
    meter(c.cliente_id, { origen: 'plan_cambios', id: c.id, fecha: c.creado_en, periodo: c.periodo, monto: Number(c.monto) || 0,
      concepto: `${conceptoDe(c.detalle) || (c.tipo === 'plan' ? 'Cambio de plan' : 'Usuario extra')}`, detalle: c.detalle, quien: c.solicitado_por, enPrueba: !!c.en_prueba });
  }
  for (const p of (b.data || [])) {
    meter(p.cliente_id, { origen: 'ia_paquetes', id: p.id, fecha: p.creado_en, periodo: p.periodo, monto: Number(p.precio) || 0,
      concepto: `Paquete de ${p.analisis} análisis con IA (${fechaCorta(p.creado_en)})`, detalle: null, quien: p.origen === 'autoservicio' ? 'comprado desde su sistema' : (p.origen || null), enPrueba: false });
  }
  for (const k of Object.keys(porCliente)) porCliente[k].sort((x, y) => String(x.fecha).localeCompare(String(y.fecha)));
  return porCliente;
}

async function evento(cliente_id, tipo, detalle, actor) {
  try { await supabase.from('eventos').insert({ cliente_id, tipo, detalle, actor }); } catch { /* no bloquear */ }
}

// Abre el documento (la MISMA plantilla que descarga el cliente) listo para imprimir / PDF.
async function abrirDocumento(id) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch('/api/estado-cuenta', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
    body: JSON.stringify({ id }),
  });
  const res = await r.json().catch(() => null);
  if (!res?.ok) throw new Error(res?.error || `No se pudo generar el documento (${r.status}).`);
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(res.html); doc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* el navegador lo bloqueó */ }
    setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* ya no está */ } }, 2000);
  }, 400);
}

// ── Emitir: qué entra en el estado de cuenta de un cliente ───────────────────────
function EmitirModal({ cliente, pendientes, cfg, actor, onClose, onHecho }) {
  const [periodo, setPeriodo] = useState(hoyPanama().slice(0, 7));
  const [sel, setSel] = useState(() => Object.fromEntries((pendientes || []).map(l => [`${l.origen}-${l.id}`, true])));
  const [otro, setOtro] = useState({ concepto: '', monto: '' });
  const [notas, setNotas] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const plan = cliente.planes;
  const extras = Number(cliente.usuarios_extra) || 0;
  const mes = nombrePeriodo(periodo);
  const lineas = [];
  if (plan) lineas.push({ concepto: `Mensualidad plan ${plan.nombre} — ${mes}`, monto: r2(plan.precio_mensual) });
  if (plan && extras > 0) lineas.push({ concepto: `${extras} usuario(s) extra — ${mes}`, monto: r2(extras * precioExtra(plan)) });
  const elegidos = (pendientes || []).filter(l => sel[`${l.origen}-${l.id}`]);
  for (const l of elegidos) if (l.monto) lineas.push({ concepto: l.concepto, monto: r2(l.monto) });
  const otroMonto = r2(otro.monto);
  if (otro.concepto.trim() && otroMonto) lineas.push({ concepto: otro.concepto.trim(), monto: otroMonto });
  const subtotal = r2(lineas.reduce((s, l) => s + l.monto, 0));
  const pct = Number(cfg?.impuesto_pct) || 0;
  const impuesto = r2(Math.max(0, subtotal) * pct / 100);

  const emitir = async () => {
    if (!/^\d{4}-\d{2}$/.test(periodo)) { setErr('Elige el mes que se cobra.'); return; }
    if (!lineas.length) { setErr('No hay nada que cobrar.'); return; }
    setBusy(true); setErr('');
    const { error } = await supabase.rpc('emitir_estado_cuenta', {
      p_cliente_id: cliente.id, p_periodo: periodo, p_lineas: lineas,
      p_cambios: elegidos.filter(l => l.origen === 'plan_cambios').map(l => l.id),
      p_paquetes: elegidos.filter(l => l.origen === 'ia_paquetes').map(l => l.id),
      p_actor: actor, p_notas: notas.trim() || null,
    });
    setBusy(false);
    if (error) {
      setErr(/estados_cuenta_un_vivo|duplicate/i.test(error.message)
        ? `Ya hay un estado de cuenta de ${mes} para este cliente. Anúlalo primero si hay que rehacerlo.`
        : 'No se pudo emitir: ' + error.message);
      return;
    }
    onHecho();
  };

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid var(--line)' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Emitir estado de cuenta</div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>{cliente.nombre}</div>
          </div>
          <button className="btn btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: 22, display: 'grid', gap: 16 }}>
          <div>
            <label className="lbl">Mes que se cobra (la mensualidad va por adelantado)</label>
            <input className="input" type="month" value={periodo} onChange={e => setPeriodo(e.target.value)} style={{ maxWidth: 220 }} />
          </div>

          {(pendientes || []).length > 0 && (
            <div>
              <label className="lbl">Ajustes que el cliente aceptó y aún no se le cobran</label>
              <div className="card" style={{ padding: '4px 0' }}>
                {pendientes.map(l => {
                  const k = `${l.origen}-${l.id}`;
                  return (
                    <label key={k} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '8px 14px', borderBottom: '1px solid var(--line)', fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!sel[k]} onChange={e => setSel(s => ({ ...s, [k]: e.target.checked }))} />
                      <span style={{ flex: 1, color: 'var(--muted)' }}>{l.concepto}</span>
                      <strong style={{ whiteSpace: 'nowrap', color: l.monto < 0 ? 'var(--success)' : l.monto ? 'var(--ink)' : 'var(--faint)' }}>{l.monto ? usd(l.monto) : (l.enPrueba ? 'en prueba' : 'sin cargo')}</strong>
                    </label>
                  );
                })}
              </div>
              <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 5 }}>Los que no tienen monto no salen en el documento, pero quedan cerrados con él.</div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 10 }}>
            <div>
              <label className="lbl">Otro concepto (opcional)</label>
              <input className="input" value={otro.concepto} onChange={e => setOtro(o => ({ ...o, concepto: e.target.value }))} placeholder="Ej.: Onboarding asistido (migración y capacitación)" />
            </div>
            <div>
              <label className="lbl">Monto US$</label>
              <input className="input" type="number" step="0.01" value={otro.monto} onChange={e => setOtro(o => ({ ...o, monto: e.target.value }))} placeholder="0.00" />
            </div>
          </div>

          <div className="card" style={{ padding: '6px 0' }}>
            {lineas.map((l, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 14px', fontSize: 13 }}>
                <span>{l.concepto}</span><strong style={{ whiteSpace: 'nowrap', color: l.monto < 0 ? 'var(--success)' : 'var(--ink)' }}>{usd(l.monto)}</strong>
              </div>
            ))}
            <div style={{ borderTop: '1px solid var(--line)', marginTop: 4, padding: '8px 14px 2px', display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--muted)' }}><span>Subtotal</span><span>{usd(subtotal)}</span></div>
            {pct > 0 && <div style={{ padding: '2px 14px', display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--muted)' }}><span>{cfg?.impuesto_nombre || 'ITBMS'} {pct}%</span><span>{usd(impuesto)}</span></div>}
            <div style={{ padding: '6px 14px 8px', display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800 }}><span>Total</span><span>{usd(subtotal + impuesto)}</span></div>
          </div>

          <div>
            <label className="lbl">Nota interna (no sale en el documento)</label>
            <input className="input" value={notas} onChange={e => setNotas(e.target.value)} />
          </div>
          {err && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 22px', borderTop: '1px solid var(--line)' }}>
          <button className="btn" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="btn btn-primary" onClick={emitir} disabled={busy}>{busy ? 'Emitiendo…' : `Emitir por ${usd(subtotal + impuesto)}`}</button>
        </div>
      </div>
    </div>
  );
}

// ── Registrar cuándo pagó ───────────────────────────────────────────────────────
function PagoModal({ ec, nombre, actor, onClose, onHecho }) {
  const [f, setF] = useState({ fecha: hoyPanama(), forma: FORMAS_PAGO[0], ref: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const guardar = async () => {
    if (!f.fecha) { setErr('Pon la fecha en que pagó.'); return; }
    setBusy(true); setErr('');
    const { error } = await supabase.from('estados_cuenta')
      .update({ estado: 'pagado', pagado_el: f.fecha, pago_forma: f.forma, pago_referencia: f.ref.trim() || null }).eq('id', ec.id);
    setBusy(false);
    if (error) { setErr('No se pudo guardar: ' + error.message); return; }
    await evento(ec.cliente_id, 'estado_cuenta_pagado', `${nombre} — ${ec.numero} pagado el ${fechaCorta(f.fecha)} (${f.forma}${f.ref.trim() ? `, ref. ${f.ref.trim()}` : ''}): ${usd(ec.total)}`, actor);
    onHecho();
  };
  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Registrar pago</div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{nombre} · {ec.numero} · {usd(ec.total)}</div>
        </div>
        <div style={{ padding: 22, display: 'grid', gap: 14 }}>
          <div><label className="lbl">¿Cuándo pagó? *</label><input className="input" type="date" value={f.fecha} onChange={e => setF(p => ({ ...p, fecha: e.target.value }))} /></div>
          <div><label className="lbl">Forma de pago</label>
            <select className="input" value={f.forma} onChange={e => setF(p => ({ ...p, forma: e.target.value }))}>{FORMAS_PAGO.map(x => <option key={x}>{x}</option>)}</select></div>
          <div><label className="lbl">Referencia / comprobante</label><input className="input" value={f.ref} onChange={e => setF(p => ({ ...p, ref: e.target.value }))} placeholder="Opcional" /></div>
          {err && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 22px', borderTop: '1px solid var(--line)' }}>
          <button className="btn" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={busy}>{busy ? 'Guardando…' : 'Registrar pago'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Datos del emisor, impuesto y cómo pagar (salen en el documento) ──────────────
function ConfigModal({ cfg, onClose, onHecho }) {
  const [f, setF] = useState(() => ({
    emisor_nombre: cfg?.emisor_nombre || '', emisor_ruc: cfg?.emisor_ruc || '', emisor_direccion: cfg?.emisor_direccion || '',
    emisor_telefono: cfg?.emisor_telefono || '', emisor_correo: cfg?.emisor_correo || '', instrucciones_pago: cfg?.instrucciones_pago || '',
    impuesto_nombre: cfg?.impuesto_nombre || 'ITBMS', impuesto_pct: cfg?.impuesto_pct ?? 7, dias_vencimiento: cfg?.dias_vencimiento ?? 10, prefijo: cfg?.prefijo || 'EC',
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const guardar = async () => {
    setBusy(true); setErr('');
    const limpio = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, typeof v === 'string' ? (v.trim() || null) : v]));
    const { error } = await supabase.from('facturacion_config').update({
      ...limpio, impuesto_pct: Number(f.impuesto_pct) || 0, dias_vencimiento: parseInt(f.dias_vencimiento, 10) || 10,
      prefijo: (f.prefijo || 'EC').trim().toUpperCase(), actualizado_en: new Date().toISOString(),
    }).eq('id', 1);
    setBusy(false);
    if (error) { setErr('No se pudo guardar: ' + error.message); return; }
    onHecho();
  };
  const campo = (k, label, ph) => (<div><label className="lbl">{label}</label><input className="input" value={f[k] ?? ''} onChange={e => set(k, e.target.value)} placeholder={ph || ''} /></div>);
  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Datos de facturación</div>
          <button className="btn btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: 22, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {campo('emisor_nombre', 'Quién emite (razón social)', 'Corredata, S.A.')}
          {campo('emisor_ruc', 'RUC / DV')}
          <div style={{ gridColumn: '1 / -1' }}>{campo('emisor_direccion', 'Dirección')}</div>
          {campo('emisor_telefono', 'Teléfono')}
          {campo('emisor_correo', 'Correo de cobros')}
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="lbl">Cómo pagar (sale en el documento mientras esté por pagar)</label>
            <textarea className="input" style={{ height: 96, padding: 10, resize: 'vertical' }} value={f.instrucciones_pago} onChange={e => set('instrucciones_pago', e.target.value)}
              placeholder={'Banco …, cuenta corriente N.º …, a nombre de …\nYappy: …\nEnvía el comprobante a …'} />
          </div>
          {campo('impuesto_nombre', 'Impuesto')}
          <div><label className="lbl">% de impuesto (0 = no cobra)</label><input className="input" type="number" step="0.01" value={f.impuesto_pct} onChange={e => set('impuesto_pct', e.target.value)} /></div>
          <div><label className="lbl">Días para pagar</label><input className="input" type="number" value={f.dias_vencimiento} onChange={e => set('dias_vencimiento', e.target.value)} /></div>
          {campo('prefijo', 'Prefijo del número', 'EC')}
          <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--faint)' }}>El % de impuesto aplica a los estados de cuenta NUEVOS. Los ya emitidos conservan el suyo.</div>
          {err && <div style={{ gridColumn: '1 / -1', color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 22px', borderTop: '1px solid var(--line)' }}>
          <button className="btn" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Una fila de estado de cuenta, con todo lo que se le puede hacer ──────────────
function FilaEstadoCuenta({ ec, nombre, actor, onCambio, mostrarCliente }) {
  const [pago, setPago] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const vencido = ec.estado === 'emitido' && ec.fecha_vencimiento && ec.fecha_vencimiento < hoyPanama();
  const chip = ec.estado === 'pagado' ? ['Pagado ' + fechaCorta(ec.pagado_el), 'var(--success)', 'var(--success-bg)']
    : ec.estado === 'anulado' ? ['Anulado', 'var(--danger)', 'var(--danger-bg)']
    : vencido ? ['Vencido ' + fechaCorta(ec.fecha_vencimiento), 'var(--danger)', 'var(--danger-bg)']
    : ['Por pagar · vence ' + fechaCorta(ec.fecha_vencimiento), 'var(--warning)', 'var(--warning-bg)'];

  const correr = async (fn) => { setBusy(true); setErr(''); try { await fn(); await onCambio?.(); } catch (e) { setErr(e.message || 'No se pudo completar.'); } finally { setBusy(false); } };
  const fiscal = () => {
    const v = window.prompt('N° de la factura fiscal que emitiste en la DGI (déjalo vacío para quitarlo):', ec.factura_fiscal_num || '');
    if (v === null) return;
    correr(async () => {
      const { error } = await supabase.from('estados_cuenta').update({ factura_fiscal_num: v.trim() || null }).eq('id', ec.id);
      if (error) throw new Error(error.message);
    });
  };
  const quitarPago = () => {
    if (!window.confirm(`¿Quitar el pago de ${ec.numero}? Volverá a "por pagar".`)) return;
    correr(async () => {
      const { error } = await supabase.from('estados_cuenta').update({ estado: 'emitido', pagado_el: null, pago_forma: null, pago_referencia: null }).eq('id', ec.id);
      if (error) throw new Error(error.message);
      await evento(ec.cliente_id, 'estado_cuenta_pago_quitado', `${nombre} — se quitó el pago de ${ec.numero}`, actor);
    });
  };
  const anular = () => {
    const motivo = window.prompt(`Anular ${ec.numero} (${usd(ec.total)}). Los ajustes que llevaba vuelven a quedar pendientes de cobro.\n\nMotivo:`);
    if (motivo === null) return;
    if (!motivo.trim()) { setErr('Escribe el motivo para anular.'); return; }
    correr(async () => {
      const { error } = await supabase.rpc('anular_estado_cuenta', { p_id: ec.id, p_motivo: motivo.trim(), p_actor: actor });
      if (error) throw new Error(error.message);
    });
  };

  return (
    <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 210px' }}>
          {mostrarCliente && <div style={{ fontWeight: 700 }}>{nombre}</div>}
          <div style={{ fontSize: 13, color: mostrarCliente ? 'var(--muted)' : 'var(--ink)', fontWeight: mostrarCliente ? 400 : 700 }}>
            <span style={{ textTransform: 'capitalize' }}>{nombrePeriodo(ec.periodo)}</span> · {ec.numero}
            {ec.factura_fiscal_num && <span style={{ color: 'var(--faint)' }}> · fiscal {ec.factura_fiscal_num}</span>}
          </div>
        </div>
        <span className="badge" style={{ background: chip[2], color: chip[1] }}>{chip[0]}</span>
        <strong style={{ minWidth: 92, textAlign: 'right' }}>{usd(ec.total)}</strong>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-sm" onClick={() => correr(() => abrirDocumento(ec.id))} disabled={busy}>PDF</button>
          {ec.estado === 'emitido' && <button className="btn btn-sm btn-primary" onClick={() => setPago(true)} disabled={busy}>Registrar pago</button>}
          {ec.estado === 'pagado' && <button className="btn btn-sm" onClick={quitarPago} disabled={busy}>Quitar pago</button>}
          {ec.estado !== 'anulado' && <button className="btn btn-sm" onClick={fiscal} disabled={busy}>{ec.factura_fiscal_num ? 'Factura fiscal' : '+ Factura fiscal'}</button>}
          {ec.estado === 'emitido' && <button className="btn btn-sm btn-danger" onClick={anular} disabled={busy}>Anular</button>}
        </div>
      </div>
      {ec.estado === 'anulado' && ec.anulado_motivo && <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 4 }}>Motivo: {ec.anulado_motivo}</div>}
      {err && <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 6 }}>{err}</div>}
      {pago && <PagoModal ec={ec} nombre={nombre} actor={actor} onClose={() => setPago(false)} onHecho={async () => { setPago(false); await onCambio?.(); }} />}
    </div>
  );
}

// ── Lo pendiente de UN cliente + el botón de emitir ──────────────────────────────
function PorCobrarCliente({ cliente, pendientes, cfg, actor, onCambio, mostrarCliente }) {
  const [emitir, setEmitir] = useState(false);
  const ajustes = r2((pendientes || []).reduce((s, l) => s + l.monto, 0));
  const enPrueba = cliente.estado === 'prueba';
  const sinPlan = !cliente.planes;
  return (
    <div className="card" style={{ marginBottom: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: (pendientes || []).length ? '1px solid var(--line)' : 'none', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          {mostrarCliente && <div style={{ fontWeight: 700 }}>{cliente.nombre}</div>}
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            {sinPlan ? 'Sin plan' : `Plan ${cliente.planes.nombre} · mensualidad ${usd(mensualidadDe(cliente))}`}
            {enPrueba && <span style={{ color: 'var(--info)' }}> · en prueba</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--faint)' }}>ajustes sin cobrar</div>
          <div style={{ fontWeight: 800, fontSize: 16, color: ajustes < 0 ? 'var(--success)' : 'var(--ink)' }}>{usd(ajustes)}</div>
        </div>
        <button className="btn btn-sm btn-primary" onClick={() => setEmitir(true)} disabled={enPrueba || sinPlan}
          title={enPrueba ? 'Está en período de prueba: pásalo a "Activo" cuando empiece a pagar' : sinPlan ? 'Primero asígnale un plan' : ''}
          style={enPrueba || sinPlan ? { opacity: .5, cursor: 'not-allowed' } : {}}>Emitir estado de cuenta</button>
      </div>
      {(pendientes || []).map(l => (
        <div key={`${l.origen}-${l.id}`} style={{ display: 'flex', gap: 12, padding: '8px 16px', borderBottom: '1px solid var(--line)', fontSize: 13, alignItems: 'baseline' }}>
          <span style={{ color: 'var(--faint)', minWidth: 78, whiteSpace: 'nowrap' }}>{fechaCorta(l.fecha)}</span>
          <span style={{ flex: 1, color: 'var(--muted)' }} title={l.detalle || ''}>{l.concepto}{l.quien && <span style={{ color: 'var(--faint)' }}> · {l.quien}</span>}</span>
          <strong style={{ whiteSpace: 'nowrap', color: l.monto < 0 ? 'var(--success)' : l.monto ? 'var(--ink)' : 'var(--faint)' }}>{l.monto ? usd(l.monto) : (l.enPrueba ? 'en prueba' : 'sin cargo')}</strong>
        </div>
      ))}
      {enPrueba && <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--faint)' }}>En prueba no se cobra. Cuando empiece a pagar, cambia su estado a “Activo” (Ver → Editar) y podrás emitirle.</div>}
      {emitir && <EmitirModal cliente={cliente} pendientes={pendientes} cfg={cfg} actor={actor} onClose={() => setEmitir(false)} onHecho={async () => { setEmitir(false); await onCambio?.(); }} />}
    </div>
  );
}

// ── Vista general ────────────────────────────────────────────────────────────────
export default function Facturacion({ actor, onSalir }) {
  const [tab, setTab] = useState('cobrar');       // 'cobrar' | 'emitidos'
  const [clientes, setClientes] = useState([]);
  const [pend, setPend] = useState({});
  const [ecs, setEcs] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [verConfig, setVerConfig] = useState(false);
  const [verAnulados, setVerAnulados] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [p, cs, es, cf] = await Promise.all([
        cargarPendientes(),
        supabase.from('clientes').select(SEL_CLIENTE).order('nombre'),
        supabase.from('estados_cuenta').select('*').order('fecha_emision', { ascending: false }).order('id', { ascending: false }).limit(200),
        supabase.from('facturacion_config').select('*').eq('id', 1).maybeSingle(),
      ]);
      if (es.error) throw new Error(es.error.message);
      setPend(p); setClientes(cs.data || []); setEcs(es.data || []); setCfg(cf.data || null);
    } catch (e) { setErr('No se pudo leer la facturación: ' + (e.message || '') + '. ¿Ya corriste schema-facturacion.sql en corredata-control?'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const nombreDe = (id) => clientes.find(c => c.id === id)?.nombre || `Cliente ${id}`;
  // A quién hay algo que cobrarle: clientes que pagan (no en prueba) o con ajustes sueltos.
  const porCobrar = clientes.filter(c => (pend[c.id] || []).length > 0 || (c.estado === 'activo' && c.planes));
  const vivos = ecs.filter(e => e.estado !== 'anulado');
  const porPagar = r2(vivos.filter(e => e.estado === 'emitido').reduce((s, e) => s + Number(e.total || 0), 0));
  const faltaEmisor = cfg && !cfg.emisor_ruc && !cfg.instrucciones_pago;

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '22px 20px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 20, fontWeight: 800 }}>Facturación</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" onClick={() => setVerConfig(true)}>Datos de facturación</button>
          <button className="btn btn-sm" onClick={onSalir}>← Volver a clientes</button>
        </div>
      </div>
      <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, maxWidth: 780, lineHeight: 1.6 }}>
        Emites el <strong>estado de cuenta</strong> del mes (mensualidad por adelantado + lo que el cliente aceptó desde su sistema + {cfg?.impuesto_nombre || 'ITBMS'}),
        registras <strong>cuándo pagó</strong> y anotas el número de tu <strong>factura fiscal</strong>. El cliente ve todo esto, solo lo suyo, en su “Mi Plan”, y descarga el mismo PDF.
      </div>
      {faltaEmisor && (
        <div className="card" style={{ padding: '10px 14px', marginBottom: 14, fontSize: 13, background: 'var(--warning-bg)', color: 'var(--warning)', borderColor: 'transparent' }}>
          Faltan tus datos de emisor (RUC, cómo pagar): los documentos saldrían incompletos. Complétalos en “Datos de facturación”.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button className={`btn btn-sm ${tab === 'cobrar' ? 'btn-primary' : ''}`} onClick={() => setTab('cobrar')}>Por emitir</button>
        <button className={`btn btn-sm ${tab === 'emitidos' ? 'btn-primary' : ''}`} onClick={() => setTab('emitidos')}>Estados de cuenta ({vivos.length})</button>
        {!loading && <span style={{ marginLeft: 'auto', fontSize: 14, color: 'var(--muted)' }}>Por cobrar: <strong style={{ color: porPagar > 0 ? 'var(--warning)' : 'var(--ink)' }}>{usd(porPagar)}</strong></span>}
      </div>

      {err && <div className="card" style={{ padding: '12px 16px', color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--faint)' }}>Cargando…</div> : tab === 'cobrar' ? (
        porCobrar.length === 0 ? (
          <div className="card" style={{ padding: 44, textAlign: 'center', color: 'var(--faint)' }}>
            <Building size={28} style={{ opacity: .5 }} />
            <div style={{ marginTop: 10, fontSize: 15, fontWeight: 600, color: 'var(--muted)' }}>Nadie a quien emitirle todavía</div>
            <div style={{ fontSize: 13 }}>Aparecen aquí los clientes en estado “Activo” y cualquiera que tenga ajustes sin cobrar.</div>
          </div>
        ) : porCobrar.map(c => <PorCobrarCliente key={c.id} cliente={c} pendientes={pend[c.id] || []} cfg={cfg} actor={actor} onCambio={load} mostrarCliente />)
      ) : (
        <>
          <div className="card" style={{ overflow: 'hidden' }}>
            {ecs.filter(e => verAnulados || e.estado !== 'anulado').length === 0
              ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--faint)' }}>Aún no has emitido ningún estado de cuenta.</div>
              : ecs.filter(e => verAnulados || e.estado !== 'anulado').map(e => <FilaEstadoCuenta key={e.id} ec={e} nombre={nombreDe(e.cliente_id)} actor={actor} onCambio={load} mostrarCliente />)}
          </div>
          {ecs.some(e => e.estado === 'anulado') && (
            <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={() => setVerAnulados(v => !v)}>{verAnulados ? 'Ocultar' : 'Ver'} anulados</button>
          )}
        </>
      )}

      {verConfig && <ConfigModal cfg={cfg} onClose={() => setVerConfig(false)} onHecho={async () => { setVerConfig(false); await load(); }} />}
    </div>
  );
}

// ── "Ver": la ficha completa de UN cliente ──────────────────────────────────────
const Dato = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 14, color: 'var(--ink)', wordBreak: 'break-word' }}>{children || <span style={{ color: 'var(--faint)' }}>—</span>}</div>
  </div>
);

export function ClienteVista({ cliente, planes, actor, badge, onEditar, onClose }) {
  const [pend, setPend] = useState([]);
  const [ecs, setEcs] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [usoIA, setUsoIA] = useState(null);
  const [activos, setActivos] = useState(undefined);   // undefined = consultando · null = no se pudo
  const [espacio, setEspacio] = useState(undefined);   // mismo criterio; solo tamaños, nunca nombres de archivo
  const [err, setErr] = useState('');

  const plan = (planes || []).find(p => p.id === cliente.plan_id) || null;
  const c = { ...cliente, planes: plan };
  const topeBase = cliente.limite_usuarios ?? plan?.limite_usuarios ?? null;
  const ilimitado = topeBase === null || Number(topeBase) === 0;
  const extras = ilimitado ? 0 : (Number(cliente.usuarios_extra) || 0);
  const tope = ilimitado ? null : Number(topeBase) + extras;

  const load = useCallback(async () => {
    setErr('');
    try {
      const periodo = hoyPanama().slice(0, 7);
      const [p, es, cf, ev, ll, pq] = await Promise.all([
        cargarPendientes(cliente.id),
        supabase.from('estados_cuenta').select('*').eq('cliente_id', cliente.id).order('periodo', { ascending: false }).order('id', { ascending: false }),
        supabase.from('facturacion_config').select('*').eq('id', 1).maybeSingle(),
        supabase.from('eventos').select('id, tipo, detalle, actor, creado_en').eq('cliente_id', cliente.id).order('creado_en', { ascending: false }).limit(15),
        supabase.from('ia_llamadas').select('id', { count: 'exact', head: true }).eq('cliente_id', cliente.id).eq('periodo', periodo).eq('cuenta_cupo', true).eq('ok', true),
        supabase.from('ia_paquetes').select('analisis').eq('cliente_id', cliente.id).eq('periodo', periodo),
      ]);
      setPend(p[cliente.id] || []); setEcs(es.data || []); setCfg(cf.data || null); setEventos(ev.data || []);
      const extraIA = (pq.data || []).reduce((s, x) => s + (Number(x.analisis) || 0), 0);
      setUsoIA({ usados: ll.count ?? 0, limite: cliente.limite_ia_mes == null ? null : (Number(cliente.limite_ia_mes) || 0) + extraIA, paquetes: extraIA });
    } catch (e) { setErr('No se pudo leer todo: ' + (e.message || '')); }
  }, [cliente.id, cliente.limite_ia_mes]);
  useEffect(() => { load(); }, [load]);

  // Usuarios ACTIVOS de verdad: se le pregunta a la base del cliente (solo el conteo).
  useEffect(() => {
    let vivo = true;
    (async () => {
      if (!cliente.supabase_ref) { setActivos(null); setEspacio(null); return; }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const r = await fetch('/api/plan', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ accion: 'uso', cliente_id: cliente.id }),
        });
        const res = await r.json().catch(() => null);
        if (vivo) { setActivos(res?.ok ? res.usuarios_activos : null); setEspacio(res?.espacio || null); }
      } catch { if (vivo) { setActivos(null); setEspacio(null); } }
    })();
    return () => { vivo = false; };
  }, [cliente.id, cliente.supabase_ref]);

  // Borra la instancia COMPLETA de un cliente en prueba (sitio + base + ficha). El servidor
  // se niega con RU, Staging y el portal, y con cualquier cliente que no esté en prueba.
  const [borrando, setBorrando] = useState(false);
  const eliminarInstancia = async () => {
    const escrito = window.prompt(
      `Se borrará TODO de "${cliente.nombre}": su sitio, su base de datos con todos sus datos, y esta ficha. No se puede deshacer.\n\nPara confirmar, escribe su ref de proyecto: ${cliente.supabase_ref}`);
    if (escrito === null) return;
    setBorrando(true); setErr('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ accion: 'eliminar_instancia', ref: cliente.supabase_ref, confirmar: escrito.trim() }),
      });
      const res = await r.json().catch(() => ({}));
      if (!r.ok || !res.ok) throw new Error(res.error || `Error ${r.status}`);
      const b = await borrarFichaCliente(cliente.id);
      if (b.error) throw new Error(`Se borró ${res.borrado.join(' y ')}, pero no la ficha: ${b.error}`);
      try { await supabase.from('eventos').insert({ cliente_id: null, tipo: 'baja', detalle: `${cliente.nombre} — instancia de prueba eliminada (${res.borrado.join(', ')})`, actor }); } catch { /* bitácora */ }
      onClose();
    } catch (e) { setErr('No se pudo eliminar: ' + (e.message || String(e))); }
    finally { setBorrando(false); }
  };

  // Clave de soporte propia del cliente (provision.js clave_soporte / aplicar_clave_soporte).
  const [soporteBusy, setSoporteBusy] = useState('');
  const [claveSop, setClaveSop] = useState('');
  const [msgSop, setMsgSop] = useState('');
  const accionSoporte = async (accion) => {
    if (accion === 'aplicar_clave_soporte' && !window.confirm(
      `Se cambiará la clave del usuario de soporte en el sistema de "${cliente.nombre}" y se redesplegará su sitio (tarda unos minutos). La clave anterior deja de servir. ¿Continuar?`)) return;
    setSoporteBusy(accion); setMsgSop('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ accion, cliente_id: cliente.id }),
      });
      const res = await r.json().catch(() => ({}));
      if (!r.ok || !res.ok) throw new Error(res.error || `Error ${r.status}`);
      if (accion === 'clave_soporte') setClaveSop(res.clave);
      else setMsgSop(`✓ Clave propia aplicada en ${res.sitio}${res.desplegado ? '. Su sitio se está redesplegando: en unos minutos queda activa.' : '. No se pudo lanzar el redespliegue: se activará con el próximo despliegue.'} Usa "Ver clave de soporte" para consultarla.`);
    } catch (e) { setMsgSop(e.message || String(e)); }
    finally { setSoporteBusy(''); }
  };

  const bloque = { fontSize: 13, fontWeight: 800, margin: '22px 0 10px' };
  const vivos = ecs.filter(e => e.estado !== 'anulado');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 860 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '18px 22px', borderBottom: '1px solid var(--line)' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 19, fontWeight: 800 }}>{cliente.nombre}</div>
              {badge}
            </div>
            {cliente.app_url && <a href={cliente.app_url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--info)' }}>{cliente.app_url.replace(/^https?:\/\//, '')}</a>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {cliente.estado === 'prueba' && cliente.supabase_ref && (
              <button className="btn btn-sm" style={{ color: 'var(--danger)' }} disabled={borrando} onClick={eliminarInstancia}
                title="Borra su sitio en Cloudflare, su proyecto Supabase (con todos sus datos) y esta ficha. Solo clientes en prueba.">
                {borrando ? 'Borrando…' : 'Eliminar instancia'}
              </button>
            )}
            <button className="btn btn-sm btn-primary" onClick={onEditar}>Editar</button>
            <button className="btn btn-sm" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        <div style={{ padding: '6px 22px 24px' }}>
          {err && <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 12 }}>{err}</div>}

          <div style={bloque}>Lo que tiene contratado</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16 }}>
            <Dato label="Plan">{plan?.nombre}</Dato>
            <Dato label="Mensualidad">
              {plan ? <>{usd(mensualidadDe(c))}{extras > 0 && <span style={{ fontSize: 12, color: 'var(--muted)' }}> ({usd(plan.precio_mensual)} + {extras} extra)</span>}</> : null}
              {cliente.estado === 'prueba' && plan && <div style={{ fontSize: 12, color: 'var(--info)' }}>en prueba: aún no se cobra</div>}
            </Dato>
            <Dato label="Usuarios">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Users size={14} />
                {activos === undefined ? '…' : activos === null ? '?' : activos} de {ilimitado ? '∞' : tope}
              </span>
              {!ilimitado && extras > 0 && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{topeBase} del plan + {extras} extra</div>}
              {cliente.limite_usuarios != null && <div style={{ fontSize: 12, color: 'var(--muted)' }}>tope especial puesto por Corredata</div>}
            </Dato>
            <Dato label="Análisis con IA (este mes)">
              {!cliente.con_ia ? 'Sin IA' : usoIA ? <>{usoIA.usados} de {usoIA.limite == null ? '∞' : usoIA.limite}{usoIA.paquetes > 0 && <div style={{ fontSize: 12, color: 'var(--muted)' }}>incluye {usoIA.paquetes} de paquetes</div>}</> : '…'}
            </Dato>
            <Dato label="Espacio de documentos">
              {espacio === undefined ? '…' : espacio === null ? '?' : (() => {
                const usados = espacio.archivos_bytes / GB;
                const lim = Number(plan?.limite_gb) || 0;
                return <>
                  <span style={{ color: lim && usados >= lim * 0.8 ? 'var(--danger)' : undefined }}>{fmtEspacio(usados)} de {lim ? `${lim} GB` : '∞'}</span>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{espacio.archivos.toLocaleString('es-PA')} archivo(s) · base de datos {fmtEspacio(espacio.base_bytes / GB)}</div>
                </>;
              })()}
            </Dato>
            <Dato label="Módulos">{Array.isArray(plan?.modulos) ? plan.modulos.filter(m => !['dashboard', 'admin'].includes(m)).join(', ') : null}</Dato>
            <Dato label="Cliente desde">{cliente.fecha_alta ? new Date(cliente.fecha_alta).toLocaleDateString('es-PA') : null}</Dato>
            {cliente.estado === 'prueba' && (
              <Dato label="Prueba hasta">
                {cliente.prueba_hasta ? String(cliente.prueba_hasta).slice(0, 10).split('-').reverse().join('/') : 'Sin vencimiento'}
                {cliente.prueba_hasta && <div style={{ fontSize: 12, color: 'var(--muted)' }}>luego 5 días de gracia y se cierra el acceso</div>}
              </Dato>
            )}
          </div>

          <div style={bloque}>Contacto e instancia</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16 }}>
            <Dato label="Contacto">{cliente.contacto_nombre}</Dato>
            <Dato label="Correo">{cliente.contacto_email}</Dato>
            <Dato label="Proyecto Supabase">{cliente.supabase_ref}</Dato>
            <Dato label="Identificador">{cliente.slug}</Dato>
            <Dato label="IA por sesión">{cliente.anon_key ? 'Lista (llave pública registrada)' : 'Falta la llave pública'}</Dato>
          </div>
          {cliente.notas && <div style={{ marginTop: 14 }}><Dato label="Notas">{cliente.notas}</Dato></div>}

          {cliente.supabase_ref && (
            <>
              <div style={bloque}>Acceso de soporte</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>
                Cada cliente tiene su propia clave para el usuario <b>soporte@corredata.com</b>. Solo sirve mientras el cliente tenga abierta una ventana de soporte (la abre su master en Administración → Usuarios); fuera de esa ventana su sistema le niega todo.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="btn btn-sm" disabled={!!soporteBusy} onClick={() => accionSoporte('clave_soporte')}>
                  {soporteBusy === 'clave_soporte' ? 'Consultando…' : 'Ver clave de soporte'}
                </button>
                <button className="btn btn-sm" disabled={!!soporteBusy} onClick={() => accionSoporte('aplicar_clave_soporte')}
                  title="Para sistemas creados antes de que existiera la clave por cliente: la escribe en su sitio y lo redespliega.">
                  {soporteBusy === 'aplicar_clave_soporte' ? 'Aplicando…' : 'Aplicar clave propia a su sistema'}
                </button>
                {claveSop && (
                  <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, padding: '4px 10px', border: '1px solid var(--line)', borderRadius: 6, userSelect: 'all' }}>{claveSop}</span>
                )}
              </div>
              {msgSop && <div style={{ fontSize: 12.5, marginTop: 8, color: msgSop.startsWith('✓') ? 'var(--success)' : 'var(--danger)' }}>{msgSop}</div>}
            </>
          )}

          <div style={bloque}>Por emitir</div>
          <PorCobrarCliente cliente={c} pendientes={pend} cfg={cfg} actor={actor} onCambio={load} />

          <div style={bloque}>Estados de cuenta</div>
          {vivos.length === 0
            ? <div style={{ fontSize: 13, color: 'var(--faint)' }}>Aún no se le ha emitido ninguno.</div>
            : <div className="card" style={{ overflow: 'hidden' }}>{vivos.map(e => <FilaEstadoCuenta key={e.id} ec={e} nombre={cliente.nombre} actor={actor} onCambio={load} />)}</div>}

          <div style={bloque}>Actividad</div>
          {eventos.length === 0 ? <div style={{ fontSize: 13, color: 'var(--faint)' }}>Sin actividad registrada.</div> : (
            <div className="card" style={{ padding: '4px 0' }}>
              {eventos.map(e => (
                <div key={e.id} style={{ display: 'flex', gap: 12, padding: '8px 14px', borderBottom: '1px solid var(--line)', fontSize: 13, alignItems: 'baseline' }}>
                  <span style={{ color: 'var(--faint)', minWidth: 96, whiteSpace: 'nowrap' }}>{new Date(e.creado_en).toLocaleString('es-PA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  <span style={{ flex: 1, color: 'var(--muted)' }}>{e.detalle || e.tipo}</span>
                  <span style={{ color: 'var(--faint)', fontSize: 12 }}>{e.actor}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
